import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'sidebar:collapsed';

/**
 * Sidebar collapsed-state hook with localStorage persistence + cross-component
 * sync.
 *
 * Uses `useSyncExternalStore` subscribing to a custom event so multiple
 * components (Header toggle + Sidebar) stay in sync without lifting state
 * to a Context provider. Toggling from any component fires the event and
 * triggers a rerender in every subscribed component.
 *
 * Falls back to `false` (expanded) if storage is unavailable.
 */

const EVENT_NAME = 'sidebar-collapsed-change';

function getSnapshot(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

// SSR-safe; treated as expanded during the brief hydration window.
function getServerSnapshot(): boolean {
  return false;
}

function subscribe(callback: () => void): () => void {
  // Listen for in-tab updates (custom event) + cross-tab updates (storage event).
  window.addEventListener(EVENT_NAME, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(EVENT_NAME, callback);
    window.removeEventListener('storage', callback);
  };
}

export function useSidebarCollapsed(): [boolean, (next: boolean) => void] {
  const collapsed = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setCollapsed = (next: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      // ignore
    }
    // Notify same-tab subscribers; storage event only fires cross-tab.
    window.dispatchEvent(new Event(EVENT_NAME));
  };

  return [collapsed, setCollapsed];
}
